/**
 * Everything in play that wears the figures' colours (spec 05 §6): the dragons, the monsters and the
 * Baron, the bubbles and items, the shots and effects, the boss - and the materials they share. Built
 * from one figure palette (the arcade's; the C64 sprites' in the 1987 look) and rebuilt whole when the
 * look changes; the colour aid and reduce flashing switch live (08 §4) with no new shader: every
 * material either of them can use is made here, up front, and warmed with the rest (GameView.warmUp).
 */

import { Color, type Material, type MeshToonMaterial, type Scene, type Texture } from 'three';
import { bossLook } from '../../sim/monsters';
import type { World } from '../../sim/types';
import { createStripedMaterial } from '../materials/aid';
import { createDitherMaterial } from '../materials/dither';
import { createEmissiveMaterial, createToonMaterial, OUTLINE, setEmissiveBoost } from '../materials/toon';
import { C64_COLOURS, type Palette } from '../palette';
import { rgb8ToHex } from '../color';
import { BossView } from './bossView';
import { BubbleViews } from './bubbleViews';
import { EffectViews } from './effectViews';
import { EntityViews, type FigureMaterials, type FigureOptions, type Previous, tickAlphas } from './entityViews';
import { flashPulse, softFlash } from './flash';

/** Self-lit effects' glow (fire, bolts, blasts): the vertex colour added this many times over. */
const GLOW = 1.6;
/** The reduce-flashing heart: a steady pink glow, and how far it pulses (softened, capped, by flash.ts). */
const HEART_GLOW = 0.3;
const HEART_PULSE = 0.9;
/** The share of a respawning player's pixels drawn with reduce flashing (a steady thinning, not a blink). */
const GHOST_COVERAGE = 0.5;
/** The C64's light blue (colour 14): a dead monster's sprite, in the 1987 look. */
const C64_LIGHT_BLUE = 14;

export class FigureSet {
  /** Self-lit material for effects (the ending's stars use it too). */
  readonly glow: MeshToonMaterial;
  /** The boss's material (the ending's boss too). */
  readonly bossMaterial: Material;
  private readonly mats: FigureMaterials;
  private readonly heartSoft: MeshToonMaterial;
  private readonly entities: EntityViews;
  private readonly bubbles: BubbleViews;
  private readonly effects: EffectViews;
  private readonly boss: BossView;

  /** `c64`: the 1987 look's figures (the C64's light blue for the dead, every bubble green). */
  constructor(scene: Scene, readonly palette: Palette, gradientMap: Texture, c64: boolean) {
    const p = palette;
    const figure = { gradientMap, depthBias: 3 };
    const lightBlue = C64_COLOURS[C64_LIGHT_BLUE];
    this.heartSoft = createToonMaterial({
      ...figure, outline: OUTLINE.player, emissive: p.color('cheek.2'), emissiveIntensity: HEART_GLOW,
    });
    this.mats = {
      player: createToonMaterial({ ...figure, outline: OUTLINE.player }),
      heart: createToonMaterial({ ...figure, outline: OUTLINE.player, tint: p.color('cheek.2') }),
      monster: createToonMaterial({ ...figure, outline: OUTLINE.character }),
      trapped: createToonMaterial({ gradientMap, outline: OUTLINE.character, depthBias: 1.5 }),
      dying: createToonMaterial({
        ...figure, outline: OUTLINE.character, tint: c64 && lightBlue ? new Color(rgb8ToHex(lightBlue)) : p.color('bob.3'),
      }),
      angry: createStripedMaterial({
        ...figure, outline: OUTLINE.character, ramp: [0, 1, 2, 3].map((i) => p.color(`angry.${i}`)),
      }),
      heartSoft: this.heartSoft,
      // No outline class: an outline round every dither hole would turn the thinning into a dark mesh.
      ghost: createDitherMaterial({ ...figure, outline: OUTLINE.none, coverage: { value: GHOST_COVERAGE } }),
    };
    // Self-lit effects sit in front of the blocks, as the C64's sprites did: bolts fly through walls.
    this.glow = createEmissiveMaterial(gradientMap, GLOW, 3);
    this.bossMaterial = this.mats.monster;
    this.entities = new EntityViews(scene, p, this.mats);
    this.bubbles = new BubbleViews(scene, p, this.mats.trapped, c64);
    this.effects = new EffectViews(scene, p, { shot: this.mats.monster, glow: this.glow });
    this.boss = new BossView(scene, p, this.mats.monster, this.mats.angry);
  }

  /** The aids and the flash cap, live. */
  setOptions(o: FigureOptions): void {
    const options = { ...o };
    this.entities.options = options;
    this.bubbles.options = options;
    this.effects.options = options;
    this.boss.options = options;
    setEmissiveBoost(this.glow, 1 + softFlash(GLOW - 1, o.reduceFlashing));
  }

  /** Place and pose everything from the world, `acc` of the way to its next sim frame. */
  sync(world: World, prev: Previous, acc: number, t: number): void {
    const a = tickAlphas(world, acc);
    this.entities.sync(world, prev, a.sprite, a.soft, t);
    this.bubbles.sync(world, prev, a.sprite, a.soft, t);
    this.effects.sync(world, prev, a.soft, t);
    this.boss.sync(bossLook(world), prev.get('boss'), a.sprite, t);
    // Only drawn with reduce flashing: the power heart as a gentle pulse under the cap.
    this.heartSoft.emissiveIntensity = HEART_GLOW + flashPulse(t, 4, HEART_PULSE, true);
  }

  /** Every material anything here can be drawn with, the aids' included (for the warm-up). */
  materials(): Material[] {
    return [...new Set([...Object.values(this.mats), this.glow, ...this.bubbles.materials(), ...this.effects.materials()])];
  }

  /** Forget every monster, bubble and item; the dragons stay. */
  clear(): void {
    this.entities.clear();
    this.bubbles.clear();
    this.effects.clear();
    this.boss.clear();
  }

  /** Take it all out of the scene and free its geometry and materials. */
  dispose(): void {
    this.boss.clear();
    this.effects.dispose();
    this.bubbles.dispose();
    this.entities.dispose();
    for (const m of new Set([...Object.values(this.mats), this.glow])) m.dispose();
  }
}
